import { useCallback, useState } from "react"
import { useEditState } from "../../App/useEditState"
import { logger } from "../../App/Logger"
import { EditGroup } from "../../Components/EditGroup"
import { BlockField, BlockRow } from "../../Components/Block"
import { Overlay, OverlayTitle } from "../../Components/Overlay"
import { useShipUserWriters } from "../ShipData/UsersDataContext"
import { userEditableFields } from "./Data"
import { InviteUserOverlayProps } from "./Params"

// ------- New User with or without invitation

export function NewUserOverlay({ ship, user, editable, shipUserCrewRoleOptions, shipUserLevelOptions, hideTypeSelection, onClose, t }: InviteUserOverlayProps) {
  // Route the create through the flag-selected writer boundary (falls back to typed-REST when
  // rendered standalone, outside a UsersView provider). (issue #1186)
  const writers = useShipUserWriters(ship.id);
  const [edited, onEdited, _resetEdited] = useEditState();
  const [userType, setUserType] = useState<number>(user?.shipUserLevel || 0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const createShipUser = useCallback(async (_data: FormData, values: any) => {
    const result = await writers.createShipUser(values);
    logger.debug("createShipUser result", result.user?.id);
    if (result.user) {
      onClose();
    } else {
      logger.error("No user returned from API");
    }
  }, [writers, onClose]);

  const handleUserTypeChange = (level: number) => {
    setUserType(level);
    // Simulate an event to update the parent state
    onEdited();
  };

  return (
    <Overlay closeCallback={onClose} closeBackground={true}>
      <OverlayTitle title={t("add_user")} closeCallback={onClose} />

      <div className="Overlay-body">
        <EditGroup key={"add-user"} changed={edited} editableFields={userEditableFields} manualSave={true} hideButton={true} onSave={createShipUser} className="editgroup_boxed">
          {/* <Block> */}
          {!hideTypeSelection && (
            <>
              <BlockRow>

                <BlockField label={t("add_user_without_permissions")} labelAfter={true} labelFor={`user_type_0`} className="bigger">
                  <input
                    type="radio"
                    id="user_type_0"
                    name="user_type"
                    checked={userType === 0}
                    onChange={() => handleUserTypeChange(0)}
                  />
                </BlockField>
              </BlockRow>

              <BlockRow>
                <BlockField label={t("add_user_with_permissions")} labelAfter={true} labelFor={`user_type_1`} className="bigger">
                  <input
                    type="radio"
                    id="user_type_1"
                    name="user_type"
                    checked={userType === 1}
                    onChange={() => handleUserTypeChange(1)}
                  />
                </BlockField>
              </BlockRow>
            </>
          )}

          <BlockRow>
            <BlockField label={t("user_name")} className="block_field_small_22_fix ">
              <input name="name" onChange={onEdited} required />
            </BlockField>
            <BlockField label={t("user_shortname")} className="block_field_small_10">
              <input name="shortname" onChange={onEdited} maxLength={10} required />
            </BlockField>
          </BlockRow>

          <BlockRow>
            <BlockField label={t("user_crew_role")} className="block_field_small_10">
              <select className="" name="shipUserCrewRole" onChange={onEdited}>
                {shipUserCrewRoleOptions}
              </select>
            </BlockField>

            {userType === 1 && (
              <BlockField label={t("ship_user_level")} className="block_field_small_22_fix">
                <select className="" name="shipUserLevel" defaultValue={user?.shipUserLevel} onChange={onEdited} disabled={!editable}>
                  {shipUserLevelOptions}
                </select>
              </BlockField>
            )}
          </BlockRow>

          {userType === 1 && (
            <BlockRow>
              <BlockField label={t("user_email")} className="block_field_small_32">
                <input name="email" onChange={onEdited} required />
              </BlockField>
            </BlockRow>
          )}

          <BlockRow actions={true}>
            <BlockField></BlockField>
            <BlockField>
              <button className="button" onClick={onClose}>{t("cancel")}</button>
              <button className="button default" type="submit">
                {userType === 1 ? t("user_invite") : t("save")}
              </button>
            </BlockField>
          </BlockRow>
          {/* </Block> */}
        </EditGroup>
      </div>
    </Overlay>
  );
}
